import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle } from 'lucide-react';
import './Auth.css';
import { BastionLockup } from './Brand';

/*
 * Register page — invite-required.
 *
 * Phase 3 of multi-tenancy: every registration must arrive with a valid
 * invite token in the URL (`?invite=TOKEN`). Without one, the page shows a
 * friendly message rather than a usable form. The future create-org wizard
 * will be a separate route for first-org registration.
 */

function Register({ onLogin }) {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(!!inviteToken);
  const [previewError, setPreviewError] = useState(null);

  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Fetch invite preview on mount
  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    axios.get(`/invites/${inviteToken}`)
      .then(res => {
        if (cancelled) return;
        setPreview(res.data);
        if (res.data.email) {
          setFormData(prev => ({ ...prev, email: res.data.email }));
        }
      })
      .catch(e => {
        if (!cancelled) setPreviewError(e.response?.data?.error || 'This invite link is not valid.');
      })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [inviteToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inviteToken) return;
    setError('');
    setLoading(true);
    try {
      const response = await axios.post('/auth/register', {
        ...formData,
        inviteToken,
      });
      onLogin(response.data.user, response.data.token);
    } catch (err) {
      setError(
        err.response?.data?.errors?.[0]?.msg ||
        err.response?.data?.error ||
        'Registration failed'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const emailLocked = !!preview?.email;

  // ── No invite token at all ──
  if (!inviteToken) {
    return (
      <div className="auth-container">
        <div className="auth-background">
          <div className="auth-gradient"></div>
        </div>
        <div className="auth-card">
          <div className="auth-header">
            <BastionLockup className="auth-lockup" variant="outline" markSize={64} />
            <h2>Registration is invite-only</h2>
            <p>Ask your administrator for an invite link to join an organization.</p>
          </div>
          <div className="auth-footer">
            <Link to="/login" className="btn btn-primary btn-block">Back to sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Invite invalid ──
  if (previewError) {
    return (
      <div className="auth-container">
        <div className="auth-background">
          <div className="auth-gradient"></div>
        </div>
        <div className="auth-card">
          <div className="auth-header">
            <BastionLockup className="auth-lockup" variant="outline" markSize={64} />
            <h2>Invite not valid</h2>
            <p>{previewError}</p>
          </div>
          <div className="auth-footer">
            <Link to="/login" className="btn btn-primary btn-block">Back to sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Loading preview ──
  if (previewLoading) {
    return (
      <div className="auth-container">
        <div className="auth-background">
          <div className="auth-gradient"></div>
        </div>
        <div className="auth-card">
          <div className="loading-spinner" style={{ margin: '60px auto' }}></div>
        </div>
      </div>
    );
  }

  // ── Valid invite, render form ──
  return (
    <div className="auth-container">
      <div className="auth-background">
        <div className="auth-gradient"></div>
      </div>

      <div className="auth-card">
        <div className="auth-header">
          <BastionLockup className="auth-lockup" variant="outline" markSize={64} />
          <p className="auth-app-name">Joining {preview.organization.name}</p>
          {preview.inviter && (
            <p>Invited by {preview.inviter.name} as a <strong>{preview.role}</strong></p>
          )}
        </div>

        {error && (
          <div className="error-message">
            <AlertCircle size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="input-group">
            <label>Full name</label>
            <input
              type="text" name="name" value={formData.name}
              onChange={handleChange} required placeholder="Your name" autoComplete="name"
              autoFocus
            />
          </div>
          <div className="input-group">
            <label>
              Email Address
              {emailLocked && <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6, fontSize: 12 }}>(from invite)</span>}
            </label>
            <input
              type="email" name="email" value={formData.email}
              onChange={handleChange} required placeholder="you@example.com" autoComplete="email"
              readOnly={emailLocked}
              style={emailLocked ? { background: 'var(--bg-elevated)', cursor: 'not-allowed' } : undefined}
            />
          </div>
          <div className="input-group">
            <label>Password</label>
            <input
              type="password" name="password" value={formData.password}
              onChange={handleChange} required minLength="6"
              placeholder="At least 6 characters" autoComplete="new-password"
            />
          </div>
          <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
            {loading ? 'Creating account…' : `Join ${preview.organization.name}`}
          </button>
        </form>

        <div className="auth-footer">
          <p>Already have an account? <Link to="/login">Sign in</Link></p>
        </div>
      </div>
    </div>
  );
}

export default Register;
